import {
  languageSchema,
  type PlatformMembership,
  unitLengthSchema,
  unitPaceSchema,
  unitSlopeSchema,
  unitTemperatureSchema,
} from "@mountaya/studio";
import { z } from "zod";
import { createStore } from "zustand/vanilla";

/**
 * preferencesSchema mirrors the user's display preferences: the i18n language that
 * drives the bootstrap and the four unit choices, each drawn from the shared
 * searchParams enum that mirrors its backend value domain. Every field is optional
 * and `.catch(undefined)`, so an absent or out-of-range value in a sparse metadata
 * payload resolves to undefined — the consumer falls back to its own default —
 * rather than failing the whole session parse, and the field set matches the auth
 * leaf's preferences shape so the value object auth hands setSession validates
 * identically here. The web app renders light-only, so there is no user-level theme
 * preference.
 */
export const preferencesSchema = z.object({
  language: languageSchema.optional().catch(undefined),
  length: unitLengthSchema.optional().catch(undefined),
  pace: unitPaceSchema.optional().catch(undefined),
  slope: unitSlopeSchema.optional().catch(undefined),
  temperature: unitTemperatureSchema.optional().catch(undefined),
});

/**
 * DisplayPreferences is the user's display preferences as the session carries them, derived
 * from preferencesSchema: the language and the four units, each optional, and no theme. It is
 * not the contract's Preferences, which the preferences endpoint answers with every field set.
 */
export type DisplayPreferences = z.infer<typeof preferencesSchema>;

/**
 * traitsSchema mirrors the Ory identity traits (email + names + newsletter).
 */
export const traitsSchema = z.object({
  email: z.string(),
  first_name: z.string(),
  last_name: z.string(),
  newsletter: z.boolean(),
});

/**
 * Traits are the Ory identity traits, derived from traitsSchema.
 */
export type Traits = z.infer<typeof traitsSchema>;

/**
 * identityMetadataSchema is the parsed metadata_public surfaced as the auth context
 * `metadata`. Zod-first so the store validates what app code hands it. A person buys
 * nothing — everything billable belongs to an organization — so display preferences
 * are the whole of what the identity carries.
 */
export const identityMetadataSchema = z.object({
  preferences: preferencesSchema,
});

/**
 * IdentityMetadata is the parsed metadata_public, derived from
 * identityMetadataSchema.
 */
export type IdentityMetadata = z.infer<typeof identityMetadataSchema>;

/**
 * identitySessionSchema is the value object the auth session bootstrap produces
 * and app code writes into the store via setSession. It is structurally the
 * IdentitySession returned by @mountayaapp/auth (defined here independently to
 * keep this package a leaf that does not import auth). emailVerified is the
 * auth-produced field name; the store surfaces it as isEmailVerified (the rename
 * happens in setSession, matching the store's is* boolean convention).
 */
export const identitySessionSchema = z.object({
  emailVerified: z.boolean(),
  metadata: identityMetadataSchema,
  preferences: preferencesSchema,
  traits: traitsSchema,
  userId: z.string(),
});

/**
 * IdentitySession is the parsed session value object, derived from
 * identitySessionSchema.
 */
export type IdentitySession = z.infer<typeof identitySessionSchema>;

/**
 * IdentityMemberships is what the membership lookup resolved for the signed-in user: the
 * active organizations they belong to (the shared root organization, which every account
 * belongs to, never among them), those of them whose agreement includes Map Studio, and what
 * Mountaya answers the user may use of the studio in each organization they are a member of.
 */
export interface IdentityMemberships {
  /** Every active organization the user is an active member of. */
  readonly organizationIds: readonly string[];
  /** The ones among them that hold Map Studio. */
  readonly studioOrganizationIds: readonly string[];
  /**
   * The answer of each membership, as the membership read carries it: the studio's features the
   * user may use in that organization and how many itineraries they may keep there (ADR 0104).
   * Kept as the platform answered it, so the viewer reads the server's answer rather than one
   * derived here; the personal space's own answer is a read of its own.
   */
  readonly studioAnswers: readonly PlatformMembership[];
}

/**
 * IdentityState is the render-free identity slice. It holds the authenticated
 * principal (userId + the hydrated traits/metadata/preferences), the active
 * organization id, and the user's memberships — client state only, never server
 * state. Auth context fields are null until the SPA session bootstrap hydrates them.
 * The memberships are set from the membership lookup once the guard resolves it: the
 * organizations the user belongs to, which of them hold Map Studio, and the answer of each,
 * which is what the studio's entitlements read. Every list is empty until the lookup lands,
 * the answer that offers nothing a membership would. isEmailVerified reflects whether the Ory identity has a verified
 * email address (verification is non-blocking, so it is false until the user verifies).
 */
export interface IdentityState extends IdentityMemberships {
  isEmailVerified: boolean;
  metadata: IdentityMetadata | null;
  organizationId: string | null;
  preferences: DisplayPreferences | null;
  traits: Traits | null;
  userId: string | null;
}

/**
 * IdentityActions are the mutation entry points for the identity slice. Per the
 * one-mutation-surface invariant, app components reach these only through the
 * tool bus — never by importing this store directly. setSession hydrates the
 * whole auth context from the bootstrap value object; reset clears it on logout.
 */
export interface IdentityActions {
  reset: () => void;
  /**
   * setMemberships records what the membership lookup resolved. A list whose members did not
   * change keeps its identity, so a reader selecting it re-renders for a change only.
   */
  setMemberships: (memberships: IdentityMemberships) => void;
  setOrganization: (organizationId: string | null) => void;
  setPreferences: (partial: Partial<DisplayPreferences>) => void;
  setSession: (session: IdentitySession) => void;
  setUser: (userId: string | null) => void;
}

export type IdentityStore = IdentityActions & IdentityState;

/** The empty membership list, hoisted so every reset answers one object. */
const noOrganizations: readonly string[] = [];

/** The empty answer list, hoisted so every reset answers one object. */
const noAnswers: readonly PlatformMembership[] = [];

/**
 * sameIds reports whether two id lists hold the same ids in the same order: the lookup answers
 * in one order, so a re-resolution that changed nothing reads equal.
 */
const sameIds = (held: readonly string[], next: readonly string[]): boolean =>
  held.length === next.length && held.every((id, index) => id === next[index]);

/**
 * sameAnswer reports whether two membership answers say the same thing: the same organization,
 * the same ceiling, and the same features in the same order, which the API sorts.
 */
const sameAnswer = (held: PlatformMembership, next: PlatformMembership): boolean =>
  held.organization_id === next.organization_id &&
  held.itinerary_limit === next.itinerary_limit &&
  sameIds(held.studio_entitlements, next.studio_entitlements);

/**
 * sameAnswers reports whether two answer lists hold the same answers in the same order, so a
 * re-resolution that changed nothing keeps the list a reader selected.
 */
const sameAnswers = (
  held: readonly PlatformMembership[],
  next: readonly PlatformMembership[],
): boolean =>
  held.length === next.length &&
  held.every((answer, index) => {
    const other = next[index];

    return other !== undefined && sameAnswer(answer, other);
  });

/**
 * copyAnswer takes a membership answer apart from the object it was handed, so a caller mutating
 * its own leaves the store alone.
 */
const copyAnswer = ({
  itinerary_limit,
  organization_id,
  studio_entitlements,
}: PlatformMembership): PlatformMembership => ({
  itinerary_limit,
  organization_id,
  studio_entitlements: [...studio_entitlements],
});

const initialState: IdentityState = {
  isEmailVerified: false,
  metadata: null,
  organizationId: null,
  organizationIds: noOrganizations,
  preferences: null,
  studioAnswers: noAnswers,
  studioOrganizationIds: noOrganizations,
  traits: null,
  userId: null,
};

/**
 * createIdentityStore builds a vanilla (render-free) Zustand store. The React
 * provider that binds it lives in @mountayaapp/theme (a sanctioned JSX
 * carve-out), keeping this package free of react-dom.
 */
export const createIdentityStore = () =>
  createStore<IdentityStore>((set) => ({
    ...initialState,
    reset: () => set(initialState),
    setMemberships: ({ organizationIds, studioAnswers, studioOrganizationIds }) =>
      set((state) => ({
        organizationIds: sameIds(state.organizationIds, organizationIds)
          ? state.organizationIds
          : [...organizationIds],
        studioAnswers: sameAnswers(state.studioAnswers, studioAnswers)
          ? state.studioAnswers
          : studioAnswers.map(copyAnswer),
        studioOrganizationIds: sameIds(state.studioOrganizationIds, studioOrganizationIds)
          ? state.studioOrganizationIds
          : [...studioOrganizationIds],
      })),
    setOrganization: (organizationId) => set({ organizationId }),
    // Merge the partial into the current preferences for client-side display
    // updates. When no preferences are hydrated yet, start from an empty base so a
    // partial update produces a valid value object; fields the partial omits stay
    // absent, and each consumer falls back to its own default.
    setPreferences: (partial) =>
      set((state) => ({
        preferences: { ...(state.preferences ?? emptyPreferences()), ...partial },
      })),
    setSession: (session) =>
      set({
        isEmailVerified: session.emailVerified,
        metadata: session.metadata,
        preferences: session.preferences,
        traits: session.traits,
        userId: session.userId,
      }),
    setUser: (userId) => set({ userId }),
  }));

export type IdentityStoreApi = ReturnType<typeof createIdentityStore>;

/**
 * emptyPreferences is the empty DisplayPreferences value object, used to seed the merge in
 * setPreferences when no preferences are hydrated yet. Every field is optional, so
 * the parse yields an object with no fields set that a partial update merges over.
 */
const emptyPreferences = (): DisplayPreferences => preferencesSchema.parse({});
